import { b2ContactPoint } from "./collision.js";
import { b2Mat22, b2Math, b2Vec2 } from "./common/math.js";
import { b2Settings } from "./common/settings.js";
import { b2Contact, b2ContactFactory, b2ContactResult } from "./dynamics_contacts.js";

// b2PairCallback is defined in dynamics_runtime.js and re-imported through b2World instantiation;
// we declare a local base here so b2ContactManager can extend it without circular imports.
class b2PairCallbackBase {
  PairAdded(_userData1, _userData2) { return null; }
  PairRemoved(_userData1, _userData2, _pairData) { }
}

function prependContactEdge(body, node) {
  node.prev = null;
  node.next = body?.m_contactList ?? null;
  if (body?.m_contactList) {
    body.m_contactList.prev = node;
  }
  if (body) {
    body.m_contactList = node;
  }
}

function getContactShape1(contact) {
  return contact.m_shape1 ?? contact.m_fixtureA?.GetShape?.() ?? contact.m_fixtureA?.m_shape ?? null;
}

function getContactShape2(contact) {
  return contact.m_shape2 ?? contact.m_fixtureB?.GetShape?.() ?? contact.m_fixtureB?.m_shape ?? null;
}

function getContactBody1(contact) {
  return getContactShape1(contact)?.m_body ?? contact.m_fixtureA?.GetBody?.() ?? contact.m_fixtureA?.m_body ?? null;
}

function getContactBody2(contact) {
  return getContactShape2(contact)?.m_body ?? contact.m_fixtureB?.GetBody?.() ?? contact.m_fixtureB?.m_body ?? null;
}

function getContactManifolds(contact) {
  if (typeof contact.GetManifolds === "function") {
    return contact.GetManifolds() ?? [];
  }
  if (typeof contact.GetManifold === "function") {
    const manifold = contact.GetManifold();
    return manifold ? [manifold] : [];
  }
  if (contact.m_manifold) {
    return [contact.m_manifold];
  }
  return [];
}

function isAwakeBody(body) {
  if (typeof body?.IsAwake === "function") {
    return body.IsAwake();
  }
  if (typeof body?.IsSleeping === "function") {
    return !body.IsSleeping();
  }
  return true;
}

function isStaticBody(body) {
  return body?.IsStatic?.() || body?.GetType?.() === 0;
}

const nullContactListener = {
  BeginContact() { },
  EndContact() { },
  PreSolve() { },
  PostSolve() { },
  Add() { },
  Remove() { },
  Persist() { },
  Result() { },
};

function debugBodyName(body) {
  return body?.GetUserData?.()?.name ?? body?.m_userData?.name ?? "-";
}

export class b2ContactConstraintPoint {
  constructor() {
    this.r2 = new b2Vec2();
    this.rB = this.r2;
    this.separation = 0;
    this.positionImpulse = 0;
    this.normalImpulse = 0;
    this.tangentMass = 0;
    this.equalizedMass = 0;
    this.tangentImpulse = 0;
    this.localAnchor1 = new b2Vec2();
    this.localAnchor2 = new b2Vec2();
    this.normalMass = 0;
    this.velocityBias = 0;
    this.r1 = new b2Vec2();
    this.rA = this.r1;
  }
}

export class b2ContactConstraint {
  constructor() {
    this.points = new Array(b2Settings.b2_maxManifoldPoints);
    this.normal = new b2Vec2();
    this.normalMass = new b2Mat22();
    this.K = new b2Mat22();
    this.restitution = 0;
    this.body1 = null;
    this.manifold = null;
    this.body2 = null;
    this.friction = 0;
    this.pointCount = 0;
    for (let i = 0; i < b2Settings.b2_maxManifoldPoints; i += 1) {
      this.points[i] = new b2ContactConstraintPoint();
    }
  }
}

export class b2ContactSolver {
  static s_debugObserver = null;

  static emitDebug(event) {
    const observer = b2ContactSolver.s_debugObserver;
    if (typeof observer !== "function") {
      return;
    }
    try {
      observer(event);
    } catch (_err) {
      // Debug hooks must never perturb simulation flow.
    }
  }

  constructor() {
    this.m_constraintCount = 0;
    this.m_constraints = [];
    this.m_allocator = null;
    this.m_step = null;
  }

  Initialize(step, contacts, contactCount, allocator) {
    this.m_step = step;
    this.m_allocator = allocator;
    this.m_constraintCount = 0;
    for (let index = 0; index < contactCount; index += 1) {
      const contact = contacts[index];
      this.m_constraintCount += contact?.m_manifoldCount ?? 0;
    }
    if (this.m_constraints.length < this.m_constraintCount) {
      this.m_constraints.length = this.m_constraintCount;
    }
    for (let index = 0; index < this.m_constraintCount; index += 1) {
      if (!this.m_constraints[index]) {
        this.m_constraints[index] = new b2ContactConstraint();
      }
    }

    let constraintIndex = 0;
    for (let index = 0; index < contactCount; index += 1) {
      const contact = contacts[index];
      const bodyA = getContactBody1(contact);
      const bodyB = getContactBody2(contact);
      const manifoldCount = contact?.m_manifoldCount ?? 0;
      const manifolds = contact.GetManifolds?.() ?? (contact.m_manifold ? [contact.m_manifold] : []);
      for (let manifoldIndex = 0; manifoldIndex < manifoldCount; manifoldIndex += 1) {
        const manifold = manifolds[manifoldIndex];
        const constraint = this.m_constraints[constraintIndex];
        constraintIndex += 1;
        constraint.bodyA = bodyA;
        constraint.bodyB = bodyB;
        constraint.body1 = bodyA;
        constraint.body2 = bodyB;
        constraint.friction = contact.m_friction ?? 0;
        constraint.restitution = contact.m_restitution ?? 0;
        constraint.flashCompat = false;
        constraint.manifold = manifold;
        constraint.pointCount = manifold?.m_pointCount ?? manifold?.pointCount ?? 0;
        if (manifold?.normal) {
          constraint.normal.SetV(manifold.normal);
        } else if (manifold?.m_normal) {
          constraint.normal.SetV(manifold.m_normal);
        } else {
          constraint.normal.SetZero();
        }
        const linearAX = bodyA.m_linearVelocity.x;
        const linearAY = bodyA.m_linearVelocity.y;
        const linearBX = bodyB.m_linearVelocity.x;
        const linearBY = bodyB.m_linearVelocity.y;
        const angularA = bodyA.m_angularVelocity;
        const angularB = bodyB.m_angularVelocity;
        for (let pointIndex = 0; pointIndex < constraint.pointCount; pointIndex += 1) {
          const manifoldPoint = manifold.m_points?.[pointIndex] ?? manifold.points?.[pointIndex];
        const point = constraint.points[pointIndex];
        // Flash AS3 uses the non-prefixed manifold impulses as solver inputs.
        // Prefer those and only fall back to m_* aliases for legacy objects.
        point.normalImpulse = manifoldPoint?.normalImpulse ?? manifoldPoint?.m_normalImpulse ?? 0;
        point.tangentImpulse = manifoldPoint?.tangentImpulse ?? manifoldPoint?.m_tangentImpulse ?? 0;
        point.separation = manifoldPoint?.separation ?? manifoldPoint?.m_separation ?? 0;
        point.positionImpulse = 0;
        point.localAnchor1.SetV(manifoldPoint?.localPoint1 ?? manifoldPoint?.m_localPoint1 ?? { x: 0, y: 0 });
        point.localAnchor2.SetV(manifoldPoint?.localPoint2 ?? manifoldPoint?.m_localPoint2 ?? { x: 0, y: 0 });
        const rotationA = bodyA.m_xf.R;
        let localAX = point.localAnchor1.x - bodyA.m_sweep.localCenter.x;
        let localAY = point.localAnchor1.y - bodyA.m_sweep.localCenter.y;
        let rotated = rotationA.col1.x * localAX + rotationA.col2.x * localAY;
        localAY = rotationA.col1.y * localAX + rotationA.col2.y * localAY;
        localAX = rotated;
        point.r1.Set(localAX, localAY);
        const rotationB = bodyB.m_xf.R;
        let localBX = point.localAnchor2.x - bodyB.m_sweep.localCenter.x;
        let localBY = point.localAnchor2.y - bodyB.m_sweep.localCenter.y;
        rotated = rotationB.col1.x * localBX + rotationB.col2.x * localBY;
        localBY = rotationB.col1.y * localBX + rotationB.col2.y * localBY;
        localBX = rotated;
        point.r2.Set(localBX, localBY);
        const rASqr = localAX * localAX + localAY * localAY;
        const rBSqr = localBX * localBX + localBY * localBY;
        const rnA = localAX * constraint.normal.x + localAY * constraint.normal.y;
        const rnB = localBX * constraint.normal.x + localBY * constraint.normal.y;
        let kNormal = bodyA.m_invMass + bodyB.m_invMass;
        kNormal += bodyA.m_invI * (rASqr - rnA * rnA) + bodyB.m_invI * (rBSqr - rnB * rnB);
        point.normalMass = 1 / kNormal;
        let kEqualized = bodyA.m_mass * bodyA.m_invMass + bodyB.m_mass * bodyB.m_invMass;
        kEqualized += bodyA.m_mass * bodyA.m_invI * (rASqr - rnA * rnA) + bodyB.m_mass * bodyB.m_invI * (rBSqr - rnB * rnB);
        point.equalizedMass = 1 / kEqualized;
        const tangentX = constraint.normal.y;
        const tangentY = -constraint.normal.x;
        const rtA = localAX * tangentX + localAY * tangentY;
        const rtB = localBX * tangentX + localBY * tangentY;
        let kTangent = bodyA.m_invMass + bodyB.m_invMass;
        kTangent += bodyA.m_invI * (rASqr - rtA * rtA) + bodyB.m_invI * (rBSqr - rtB * rtB);
        point.tangentMass = 1 / kTangent;
        point.velocityBias = 0;
        if (point.separation > 0) {
          point.velocityBias = -60 * point.separation;
        }
        const dvX = linearBX - angularB * localBY - linearAX + angularA * localAY;
        const dvY = linearBY + angularB * localBX - linearAY - angularA * localAX;
        const relativeVelocity = constraint.normal.x * dvX + constraint.normal.y * dvY;
        if (relativeVelocity < -b2Settings.b2_velocityThreshold) {
          point.velocityBias += -constraint.restitution * relativeVelocity;
        }
        }
      }
    }
  }

  InitVelocityConstraints(step) {
    for (let index = 0; index < this.m_constraintCount; index += 1) {
      const constraint = this.m_constraints[index];
      const bodyA = constraint.bodyA;
      const bodyB = constraint.bodyB;
      const wABefore = bodyA?.GetAngularVelocity?.() ?? bodyA?.m_angularVelocity ?? 0;
      const wBBefore = bodyB?.GetAngularVelocity?.() ?? bodyB?.m_angularVelocity ?? 0;
      const normalX = constraint.normal.x;
      const normalY = constraint.normal.y;
      const tangentX = normalY;
      const tangentY = -normalX;
      for (let pointIndex = 0; pointIndex < constraint.pointCount; pointIndex += 1) {
        const point = constraint.points[pointIndex];
        if (step.warmStarting) {
          point.normalImpulse *= step.dtRatio;
          point.tangentImpulse *= step.dtRatio;
          const impulseX = point.normalImpulse * normalX + point.tangentImpulse * tangentX;
          const impulseY = point.normalImpulse * normalY + point.tangentImpulse * tangentY;
          bodyA.m_angularVelocity -= bodyA.m_invI * (point.r1.x * impulseY - point.r1.y * impulseX);
          bodyA.m_linearVelocity.x -= bodyA.m_invMass * impulseX;
          bodyA.m_linearVelocity.y -= bodyA.m_invMass * impulseY;
          bodyB.m_angularVelocity += bodyB.m_invI * (point.r2.x * impulseY - point.r2.y * impulseX);
          bodyB.m_linearVelocity.x += bodyB.m_invMass * impulseX;
          bodyB.m_linearVelocity.y += bodyB.m_invMass * impulseY;
        } else {
          point.normalImpulse = 0;
          point.tangentImpulse = 0;
        }
      }
      const wAAfter = bodyA?.GetAngularVelocity?.() ?? bodyA?.m_angularVelocity ?? 0;
      const wBAfter = bodyB?.GetAngularVelocity?.() ?? bodyB?.m_angularVelocity ?? 0;
      const point0 = constraint.points?.[0] ?? null;
      b2ContactSolver.emitDebug({
        kind: "contact_init_delta",
        cidx: index,
        bodyA,
        bodyB,
        pair: `${debugBodyName(bodyA)}>${debugBodyName(bodyB)}`,
        dw1: wAAfter - wABefore,
        dw2: wBAfter - wBBefore,
        pc: constraint.pointCount ?? 0,
        ni0: point0?.normalImpulse ?? 0,
        ti0: point0?.tangentImpulse ?? 0,
        id0: point0?.id?._key ?? 0,
      });
    }
  }

  SolvePositionConstraints(baumgarte) {
    let minSeparation = 0;
    for (let index = 0; index < this.m_constraintCount; index += 1) {
      const constraint = this.m_constraints[index];
      const bodyA = constraint.bodyA;
      const bodyB = constraint.bodyB;
      const invMassA = bodyA.m_mass * bodyA.m_invMass;
      const invIA = bodyA.m_mass * bodyA.m_invI;
      const invMassB = bodyB.m_mass * bodyB.m_invMass;
      const invIB = bodyB.m_mass * bodyB.m_invI;

      if (!constraint.flashCompat) {
        const normal = constraint.normal;
        for (let pointIndex = 0; pointIndex < constraint.pointCount; pointIndex += 1) {
          const point = constraint.points[pointIndex];
          const rotationA = bodyA.m_xf.R;
          let localAX = point.localAnchor1.x - bodyA.m_sweep.localCenter.x;
          let localAY = point.localAnchor1.y - bodyA.m_sweep.localCenter.y;
          let rotated = rotationA.col1.x * localAX + rotationA.col2.x * localAY;
          localAY = rotationA.col1.y * localAX + rotationA.col2.y * localAY;
          localAX = rotated;
          const rotationB = bodyB.m_xf.R;
          let localBX = point.localAnchor2.x - bodyB.m_sweep.localCenter.x;
          let localBY = point.localAnchor2.y - bodyB.m_sweep.localCenter.y;
          rotated = rotationB.col1.x * localBX + rotationB.col2.x * localBY;
          localBY = rotationB.col1.y * localBX + rotationB.col2.y * localBY;
          localBX = rotated;
          const pointAX = bodyA.m_sweep.c.x + localAX;
          const pointAY = bodyA.m_sweep.c.y + localAY;
          const pointBX = bodyB.m_sweep.c.x + localBX;
          const pointBY = bodyB.m_sweep.c.y + localBY;
          const separation = (pointBX - pointAX) * normal.x + (pointBY - pointAY) * normal.y + point.separation;
          minSeparation = Math.min(minSeparation, separation);
          const correction = baumgarte * b2Math.Clamp(
            separation + b2Settings.b2_linearSlop,
            -b2Settings.b2_maxLinearCorrection,
            0,
          );
          let impulse = -point.equalizedMass * correction;
          const previousImpulse = point.positionImpulse;
          point.positionImpulse = Math.max(previousImpulse + impulse, 0);
          impulse = point.positionImpulse - previousImpulse;
          const px = impulse * normal.x;
          const py = impulse * normal.y;
          bodyA.m_sweep.c.x -= invMassA * px;
          bodyA.m_sweep.c.y -= invMassA * py;
          bodyA.m_sweep.a -= invIA * (localAX * py - localAY * px);
          bodyA.SynchronizeTransform();
          bodyB.m_sweep.c.x += invMassB * px;
          bodyB.m_sweep.c.y += invMassB * py;
          bodyB.m_sweep.a += invIB * (localBX * py - localBY * px);
          bodyB.SynchronizeTransform();
        }
        continue;
      }

      const point = constraint.points[0];
      const normal = constraint.normal;
      const offsetA = {
        x: bodyA.m_xf.R.col1.x * (point.flashLocalAnchorA.x - bodyA.m_sweep.localCenter.x) + bodyA.m_xf.R.col2.x * (point.flashLocalAnchorA.y - bodyA.m_sweep.localCenter.y),
        y: bodyA.m_xf.R.col1.y * (point.flashLocalAnchorA.x - bodyA.m_sweep.localCenter.x) + bodyA.m_xf.R.col2.y * (point.flashLocalAnchorA.y - bodyA.m_sweep.localCenter.y),
      };
      const offsetB = {
        x: bodyB.m_xf.R.col1.x * (point.flashLocalAnchorB.x - bodyB.m_sweep.localCenter.x) + bodyB.m_xf.R.col2.x * (point.flashLocalAnchorB.y - bodyB.m_sweep.localCenter.y),
        y: bodyB.m_xf.R.col1.y * (point.flashLocalAnchorB.x - bodyB.m_sweep.localCenter.x) + bodyB.m_xf.R.col2.y * (point.flashLocalAnchorB.y - bodyB.m_sweep.localCenter.y),
      };
      const pointAX = bodyA.m_sweep.c.x + offsetA.x;
      const pointAY = bodyA.m_sweep.c.y + offsetA.y;
      const pointBX = bodyB.m_sweep.c.x + offsetB.x;
      const pointBY = bodyB.m_sweep.c.y + offsetB.y;
      const separation = (pointBX - pointAX) * normal.x + (pointBY - pointAY) * normal.y + point.flashSeparation;
      minSeparation = Math.min(minSeparation, separation);
      let correction = baumgarte * b2Math.Clamp(
        separation + b2Settings.b2_linearSlop,
        -b2Settings.b2_maxLinearCorrection,
        0,
      );
      let impulse = -point.equalizedMass * correction;
      const previousImpulse = point.positionImpulse;
      point.positionImpulse = Math.max(previousImpulse + impulse, 0);
      impulse = point.positionImpulse - previousImpulse;
      const px = impulse * normal.x;
      const py = impulse * normal.y;
      bodyA.m_sweep.c.x -= invMassA * px;
      bodyA.m_sweep.c.y -= invMassA * py;
      bodyA.m_sweep.a -= invIA * (offsetA.x * py - offsetA.y * px);
      bodyA.SynchronizeTransform();
      bodyB.m_sweep.c.x += invMassB * px;
      bodyB.m_sweep.c.y += invMassB * py;
      bodyB.m_sweep.a += invIB * (offsetB.x * py - offsetB.y * px);
      bodyB.SynchronizeTransform();
    }
    return minSeparation >= -1.5 * b2Settings.b2_linearSlop;
  }

  SolveVelocityConstraints() {
    for (let index = 0; index < this.m_constraintCount; index += 1) {
      const constraint = this.m_constraints[index];
      const bodyA = constraint.bodyA;
      const bodyB = constraint.bodyB;
      let wA = bodyA.m_angularVelocity;
      let wB = bodyB.m_angularVelocity;
      const vA = bodyA.m_linearVelocity;
      const vB = bodyB.m_linearVelocity;
      const invMassA = bodyA.m_invMass;
      const invIA = bodyA.m_invI;
      const invMassB = bodyB.m_invMass;
      const invIB = bodyB.m_invI;
      const normalX = constraint.normal.x;
      const normalY = constraint.normal.y;
      const tangentX = normalY;
      const tangentY = -normalX;
      const friction = constraint.friction;

      if (constraint.flashCompat && constraint.pointCount === 1) {
        const point = constraint.points[0];
        const dvX = vB.x - wB * point.rB.y - vA.x + wA * point.rA.y;
        const dvY = vB.y + wB * point.rB.x - vA.y - wA * point.rA.x;
        let normalImpulseDelta = -point.normalMass * ((dvX * normalX + dvY * normalY) - point.velocityBias);
        let tangentImpulseDelta = point.tangentMass * -(dvX * tangentX + dvY * tangentY);
        const nextNormalImpulse = Math.max(point.normalImpulse + normalImpulseDelta, 0);
        normalImpulseDelta = nextNormalImpulse - point.normalImpulse;
        const maxFriction = friction * point.normalImpulse;
        const nextTangentImpulse = b2Math.Clamp(point.tangentImpulse + tangentImpulseDelta, -maxFriction, maxFriction);
        tangentImpulseDelta = nextTangentImpulse - point.tangentImpulse;
        const impulseX = normalImpulseDelta * normalX + tangentImpulseDelta * tangentX;
        const impulseY = normalImpulseDelta * normalY + tangentImpulseDelta * tangentY;
        vA.x -= invMassA * impulseX;
        vA.y -= invMassA * impulseY;
        wA -= invIA * (point.rA.x * impulseY - point.rA.y * impulseX);
        vB.x += invMassB * impulseX;
        vB.y += invMassB * impulseY;
        wB += invIB * (point.rB.x * impulseY - point.rB.y * impulseX);
        point.normalImpulse = nextNormalImpulse;
        point.tangentImpulse = nextTangentImpulse;
        bodyA.m_angularVelocity = wA;
        bodyB.m_angularVelocity = wB;
        continue;
      }

      for (let pointIndex = 0; pointIndex < constraint.pointCount; pointIndex += 1) {
        const point = constraint.points[pointIndex];
        const dvX = vB.x - wB * point.r2.y - vA.x + wA * point.r1.y;
        const dvY = vB.y + wB * point.r2.x - vA.y - wA * point.r1.x;
        const normalVelocity = dvX * normalX + dvY * normalY;
        let normalImpulse = -point.normalMass * (normalVelocity - point.velocityBias);
        const nextNormalImpulse = Math.max(point.normalImpulse + normalImpulse, 0);
        normalImpulse = nextNormalImpulse - point.normalImpulse;
        const tangentVelocity = dvX * tangentX + dvY * tangentY;
        let tangentImpulse = point.tangentMass * -tangentVelocity;
        const maxFriction = friction * point.normalImpulse;
        const nextTangentImpulse = b2Math.Clamp(point.tangentImpulse + tangentImpulse, -maxFriction, maxFriction);
        tangentImpulse = nextTangentImpulse - point.tangentImpulse;
        const impulseX = normalImpulse * normalX + tangentImpulse * tangentX;
        const impulseY = normalImpulse * normalY + tangentImpulse * tangentY;
        vA.x -= invMassA * impulseX;
        vA.y -= invMassA * impulseY;
        wA -= invIA * (point.r1.x * impulseY - point.r1.y * impulseX);
        vB.x += invMassB * impulseX;
        vB.y += invMassB * impulseY;
        wB += invIB * (point.r2.x * impulseY - point.r2.y * impulseX);
        point.normalImpulse = nextNormalImpulse;
        point.tangentImpulse = nextTangentImpulse;
      }

      bodyA.m_angularVelocity = wA;
      bodyB.m_angularVelocity = wB;
    }
  }

  FinalizeVelocityConstraints() {
    for (let index = 0; index < this.m_constraintCount; index += 1) {
      const constraint = this.m_constraints[index];
      const manifold = constraint.manifold;
      if (!manifold) {
        continue;
      }
      for (let pointIndex = 0; pointIndex < constraint.pointCount; pointIndex += 1) {
        const manifoldPoint = manifold.m_points?.[pointIndex] ?? manifold.points?.[pointIndex];
        if (!manifoldPoint) {
          continue;
        }
        const point = constraint.points[pointIndex];
        manifoldPoint.normalImpulse = point.normalImpulse;
        manifoldPoint.tangentImpulse = point.tangentImpulse;
        manifoldPoint.m_normalImpulse = point.normalImpulse;
        manifoldPoint.m_tangentImpulse = point.tangentImpulse;
        manifoldPoint.normalImpulse = point.normalImpulse;
        manifoldPoint.tangentImpulse = point.tangentImpulse;
      }
    }
  }
}

b2ContactSolver.s_psm = {
  Initialize() { },
  m_normal: new b2Vec2(),
  m_points: [new b2Vec2(), new b2Vec2()],
  m_separations: [0, 0],
};

export class b2Island {
  static s_reportCR = new b2ContactResult();
  static s_debugObserver = null;

  static emitDebug(event) {
    const observer = b2Island.s_debugObserver;
    if (typeof observer !== "function") {
      return;
    }
    try {
      observer(event);
    } catch (_err) {
      // Debug hooks must never perturb simulation flow.
    }
  }

  Initialize(bodyCapacity, contactCapacity, jointCapacity, allocator, listener, contactSolver = null) {
    this.m_bodyCapacity = bodyCapacity;
    this.m_contactCapacity = contactCapacity;
    this.m_jointCapacity = jointCapacity;
    this.m_allocator = allocator;
    this.m_listener = listener;
    this.m_bodies = this.m_bodies ?? new Array(bodyCapacity);
    this.m_contacts = this.m_contacts ?? new Array(contactCapacity);
    this.m_joints = this.m_joints ?? new Array(jointCapacity);
    this.m_bodyCount = 0;
    this.m_contactCount = 0;
    this.m_jointCount = 0;
    this.m_positionIterationCount = 0;
    this.m_contactSolver = contactSolver ?? this.m_contactSolver ?? new b2ContactSolver();
  }

  Clear() {
    this.m_bodyCount = 0;
    this.m_contactCount = 0;
    this.m_jointCount = 0;
  }

  AddBody(body) {
    this.m_bodies[this.m_bodyCount++] = body;
  }

  AddContact(contact) {
    this.m_contacts[this.m_contactCount++] = contact;
  }

  AddJoint(joint) {
    this.m_joints[this.m_jointCount++] = joint;
  }

  Report(constraints) {
    if (this.m_listener == null) {
      return;
    }
    for (let index = 0; index < this.m_contactCount; index += 1) {
      const contact = this.m_contacts[index];
      const constraint = constraints[index];
      const result = b2Island.s_reportCR;
      result.shape1 = getContactShape1(contact);
      result.shape2 = getContactShape2(contact);
      const body1 = result.shape1?.m_body;
      const manifolds = getContactManifolds(contact);
      for (let manifoldIndex = 0; manifoldIndex < manifolds.length; manifoldIndex += 1) {
        const manifold = manifolds[manifoldIndex];
        result.normal.SetV(manifold.normal ?? manifold.m_normal ?? manifold.m_localPlaneNormal);
        for (let pointIndex = 0; pointIndex < (manifold.m_pointCount ?? manifold.pointCount ?? 0); pointIndex += 1) {
          const manifoldPoint = manifold.m_points?.[pointIndex] ?? manifold.points?.[pointIndex];
          const constraintPoint = constraint.points[pointIndex];
          result.position = body1.GetWorldPoint(manifoldPoint.m_localPoint ?? manifoldPoint.localPoint1);
          result.normalImpulse = constraintPoint.normalImpulse;
          result.tangentImpulse = constraintPoint.tangentImpulse;
          result.id.key = manifoldPoint.m_id?.key ?? manifoldPoint.id?.key ?? manifoldPoint.m_id?._key ?? manifoldPoint.id?._key ?? 0;
          this.m_listener.Result(result);
        }
      }
    }
  }

  Solve(step, gravity, allowSleep) {
    const maxIterations = step.maxIterations;
    for (let index = 0; index < this.m_bodyCount; index += 1) {
      const body = this.m_bodies[index];
      if (isStaticBody(body)) {
        continue;
      }
      body.m_linearVelocity.x += step.dt * (gravity.x + body.m_invMass * body.m_force.x);
      body.m_linearVelocity.y += step.dt * (gravity.y + body.m_invMass * body.m_force.y);
      body.m_angularVelocity += step.dt * body.m_invI * body.m_torque;
      body.m_force.SetZero();
      body.m_torque = 0;
      body.m_linearVelocity.Multiply(b2Math.b2Clamp(1 - step.dt * body.m_linearDamping, 0, 1));
      body.m_angularVelocity *= b2Math.b2Clamp(1 - step.dt * body.m_angularDamping, 0, 1);
      if (body.m_linearVelocity.LengthSquared() > b2Settings.b2_maxLinearVelocitySquared) {
        body.m_linearVelocity.Normalize();
        body.m_linearVelocity.x *= b2Settings.b2_maxLinearVelocity;
        body.m_linearVelocity.y *= b2Settings.b2_maxLinearVelocity;
      }
      if (body.m_angularVelocity * body.m_angularVelocity > b2Settings.b2_maxAngularVelocitySquared) {
        body.m_angularVelocity = body.m_angularVelocity < 0 ? -b2Settings.b2_maxAngularVelocity : b2Settings.b2_maxAngularVelocity;
      }
    }

    const contactSolver = new b2ContactSolver();
    contactSolver.Initialize(step, this.m_contacts, this.m_contactCount, this.m_allocator);
    b2Island.emitDebug({
      kind: "step",
      phase: "solve",
      stage: "pre_init",
      iter: -1,
      island: this,
      contactSolver,
      step,
    });
    contactSolver.InitVelocityConstraints(step);
    b2Island.emitDebug({
      kind: "step",
      phase: "solve",
      stage: "post_contact_init",
      iter: -1,
      island: this,
      contactSolver,
      step,
    });
    for (let index = 0; index < this.m_jointCount; index += 1) {
      const joint = this.m_joints[index];
      const body1 = joint?.m_body1 ?? joint?.m_bodyA ?? null;
      const body2 = joint?.m_body2 ?? joint?.m_bodyB ?? null;
      const w1Before = body1?.GetAngularVelocity?.() ?? body1?.m_angularVelocity ?? 0;
      const w2Before = body2?.GetAngularVelocity?.() ?? body2?.m_angularVelocity ?? 0;
      joint.InitVelocityConstraints(step);
      const w1After = body1?.GetAngularVelocity?.() ?? body1?.m_angularVelocity ?? 0;
      const w2After = body2?.GetAngularVelocity?.() ?? body2?.m_angularVelocity ?? 0;
      b2Island.emitDebug({
        kind: "joint_init_delta",
        phase: "solve",
        iter: -1,
        jointIndex: index,
        island: this,
        joint,
        body1,
        body2,
        deltaW1: w1After - w1Before,
        deltaW2: w2After - w2Before,
      });
    }
    b2Island.emitDebug({
      kind: "step",
      phase: "solve",
      stage: "post_init",
      iter: -1,
      island: this,
      contactSolver,
      step,
    });
    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
      b2Island.emitDebug({
        kind: "step",
        phase: "solve",
        stage: "iter_pre",
        iter: iteration,
        island: this,
        contactSolver,
        step,
      });
      contactSolver.SolveVelocityConstraints();
      for (let jointIndex = 0; jointIndex < this.m_jointCount; jointIndex += 1) {
        const joint = this.m_joints[jointIndex];
        const body2 = joint?.m_body2 ?? joint?.m_bodyB ?? null;
        const angleBefore = body2?.m_sweep?.a;
        joint.SolveVelocityConstraints(step);
        const angleAfter = body2?.m_sweep?.a;
        if (angleBefore != null && angleAfter != null) {
          b2Island.emitDebug({
            kind: "joint_delta",
            phase: "solve",
            iter: iteration,
            jointIndex,
            island: this,
            joint,
            body2,
            deltaAngle: angleAfter - angleBefore,
          });
        }
      }
      b2Island.emitDebug({
        kind: "step",
        phase: "solve",
        stage: "iter_post",
        iter: iteration,
        island: this,
        contactSolver,
        step,
      });
    }
    for (let index = 0; index < this.m_jointCount; index += 1) {
      this.m_joints[index].FinalizeVelocityConstraints();
    }
    contactSolver.FinalizeVelocityConstraints();
    b2Island.emitDebug({
      kind: "step",
      phase: "solve",
      stage: "finalize",
      iter: -1,
      island: this,
      contactSolver,
      step,
    });
    for (let index = 0; index < this.m_bodyCount; index += 1) {
      const body = this.m_bodies[index];
      if (isStaticBody(body)) {
        continue;
      }
      body.m_sweep.c0.SetV(body.m_sweep.c);
      body.m_sweep.a0 = body.m_sweep.a;
      body.m_sweep.c.x += step.dt * body.m_linearVelocity.x;
      body.m_sweep.c.y += step.dt * body.m_linearVelocity.y;
      body.m_sweep.a += step.dt * body.m_angularVelocity;
      body.SynchronizeTransform();
    }
    this.m_positionIterationCount = 0;
    if (step.positionCorrection !== false) {
      for (let index = 0; index < this.m_jointCount; index += 1) {
        this.m_joints[index].InitPositionConstraints();
      }
      for (let iteration = 0; iteration < maxIterations; iteration += 1) {
        b2Island.emitDebug({
          kind: "step",
          phase: "solve",
          stage: "pos_iter_pre",
          iter: iteration,
          island: this,
          contactSolver,
          step,
        });
        const contactsOkay = contactSolver.SolvePositionConstraints(b2Settings.b2_contactBaumgarte);
        b2Island.emitDebug({
          kind: "step",
          phase: "solve",
          stage: "pos_iter_contact_post",
          iter: iteration,
          island: this,
          contactSolver,
          step,
        });
        let jointsOkay = true;
        for (let jointIndex = 0; jointIndex < this.m_jointCount; jointIndex += 1) {
          const jointOkay = this.m_joints[jointIndex].SolvePositionConstraints();
          jointsOkay = jointsOkay && jointOkay;
        }
        b2Island.emitDebug({
          kind: "step",
          phase: "solve",
          stage: "pos_iter_post",
          iter: iteration,
          island: this,
          contactSolver,
          step,
        });
        if (contactsOkay && jointsOkay) {
          break;
        }
        this.m_positionIterationCount = iteration + 1;
      }
    }
    this.Report(contactSolver.m_constraints);
    if (!allowSleep) {
      return;
    }
    let minSleepTime = Number.MAX_VALUE;
    const linearSleepTolerance = b2Settings.b2_linearSleepTolerance * b2Settings.b2_linearSleepTolerance;
    const angularSleepTolerance = b2Settings.b2_angularSleepTolerance * b2Settings.b2_angularSleepTolerance;
    for (let index = 0; index < this.m_bodyCount; index += 1) {
      const body = this.m_bodies[index];
      if (body.m_invMass === 0) {
        continue;
      }
      if ((body.m_flags & body.constructor.e_allowSleepFlag) === 0
        || body.m_angularVelocity * body.m_angularVelocity > angularSleepTolerance
        || b2Math.Dot(body.m_linearVelocity, body.m_linearVelocity) > linearSleepTolerance) {
        body.m_sleepTime = 0;
        minSleepTime = 0;
      } else {
        body.m_sleepTime += step.dt;
        minSleepTime = b2Math.Min(minSleepTime, body.m_sleepTime);
      }
    }
    if (minSleepTime < b2Settings.b2_timeToSleep) {
      return;
    }
    for (let index = 0; index < this.m_bodyCount; index += 1) {
      const body = this.m_bodies[index];
      body.m_flags |= body.constructor.e_sleepFlag;
      body.m_linearVelocity.SetZero();
      body.m_angularVelocity = 0;
    }
  }

  SolveTOI(subStep) {
    const maxIterations = subStep.maxIterations;
    const contactSolver = new b2ContactSolver();
    contactSolver.Initialize(subStep, this.m_contacts, this.m_contactCount, this.m_allocator);
    b2Island.emitDebug({
      kind: "step",
      phase: "toi",
      stage: "pre_init",
      iter: -1,
      island: this,
      contactSolver,
      step: subStep,
    });
    b2Island.emitDebug({
      kind: "step",
      phase: "toi",
      stage: "post_init",
      iter: -1,
      island: this,
      contactSolver,
      step: subStep,
    });
    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
      b2Island.emitDebug({
        kind: "step",
        phase: "toi",
        stage: "iter_pre",
        iter: iteration,
        island: this,
        contactSolver,
        step: subStep,
      });
      contactSolver.SolveVelocityConstraints();
      b2Island.emitDebug({
        kind: "step",
        phase: "toi",
        stage: "iter_post",
        iter: iteration,
        island: this,
        contactSolver,
        step: subStep,
      });
    }
    b2Island.emitDebug({
      kind: "step",
      phase: "toi",
      stage: "finalize",
      iter: -1,
      island: this,
      contactSolver,
      step: subStep,
    });
    for (let index = 0; index < this.m_bodyCount; index += 1) {
      const body = this.m_bodies[index];
      if (isStaticBody(body)) {
        continue;
      }
      body.m_sweep.c0.SetV(body.m_sweep.c);
      body.m_sweep.a0 = body.m_sweep.a;
      body.m_sweep.c.x += subStep.dt * body.m_linearVelocity.x;
      body.m_sweep.c.y += subStep.dt * body.m_linearVelocity.y;
      body.m_sweep.a += subStep.dt * body.m_angularVelocity;
      body.SynchronizeTransform();
    }
    const toiBaumgarte = 0.75;
    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
      if (contactSolver.SolvePositionConstraints(toiBaumgarte)) {
        break;
      }
    }
    this.Report(contactSolver.m_constraints);
  }
}

// AS3-style b2ContactManager — extends b2PairCallback so the BroadPhase calls us
export class b2ContactManager extends b2PairCallbackBase {
  static s_evalCP = new b2ContactPoint();

  constructor() {
    super();
    this.m_world = null;
    this.m_nullContact = { m_manifoldCount: 0, _isNull: true };
    this.m_destroyImmediate = false;
  }

  // Called by b2PairManager.Commit() when two proxies start overlapping.
  // userData1/2 can be b2Shape objects (from b2Shape.CreateProxy) or b2Fixture objects
  // (from b2Fixture.CreateProxy) depending on which world API the game uses.
  PairAdded(userData1, userData2) {
    // Normalize: get the shape from either a shape or a fixture.
    const shape1 = userData1?.m_shape ?? (typeof userData1?.GetShape === "function" ? userData1.GetShape() : userData1);
    const shape2 = userData2?.m_shape ?? (typeof userData2?.GetShape === "function" ? userData2.GetShape() : userData2);

    if (!shape1 || !shape2) return this.m_nullContact;

    const body1 = shape1.m_body ?? userData1?.GetBody?.() ?? userData1?.m_body;
    const body2 = shape2.m_body ?? userData2?.GetBody?.() ?? userData2?.m_body;

    if (!body1 || !body2) return this.m_nullContact;

    // Both static — no contact needed.
    if (body1.IsStatic() && body2.IsStatic()) {
      return this.m_nullContact;
    }
    // Same body — skip.
    if (body1 === body2) {
      return this.m_nullContact;
    }
    // Connected by a joint — respect collideConnected.
    if (body2.IsConnected && body2.IsConnected(body1)) {
      return this.m_nullContact;
    }
    // Contact filter.
    if (this.m_world.m_contactFilter && !this.m_world.m_contactFilter.ShouldCollide(shape1, shape2)) {
      return this.m_nullContact;
    }

    // Create a contact between the two shapes.
    const contact = b2Contact.Create(shape1, shape2, this.m_world.m_blockAllocator);
    if (contact === null) {
      return this.m_nullContact;
    }

    // Canonical shape/body references (b2Contact.Create may swap to ensure type ordering).
    const s1 = contact.m_shape1;
    const s2 = contact.m_shape2;
    const b1 = s1.m_body;
    const b2_ = s2.m_body;

    // Prepend to world contact list.
    contact.m_prev = null;
    contact.m_next = this.m_world.m_contactList;
    if (this.m_world.m_contactList !== null) {
      this.m_world.m_contactList.m_prev = contact;
    }
    this.m_world.m_contactList = contact;

    // Wire contact edge nodes (AS3 uses m_node1/m_node2).
    contact.m_node1.contact = contact;
    contact.m_node1.other = b2_;
    prependContactEdge(b1, contact.m_node1);

    contact.m_node2.contact = contact;
    contact.m_node2.other = b1;
    prependContactEdge(b2_, contact.m_node2);

    this.m_world.m_contactCount += 1;
    return contact;
  }

  // Called by b2PairManager.Commit() when two proxies stop overlapping.
  PairRemoved(userData1, userData2, pairData) {
    if (pairData === null || pairData === undefined) return;
    const contact = pairData;
    if (contact._isNull) return;
    this.Destroy(contact);
  }

  Destroy(contact) {
    const shape1 = getContactShape1(contact);
    const shape2 = getContactShape2(contact);
    const manifoldCount = contact.m_manifoldCount ?? 0;
    if (manifoldCount > 0 && this.m_world?.m_contactListener) {
      const body1 = getContactBody1(contact);
      const body2 = getContactBody2(contact);
      const manifolds = getContactManifolds(contact);
      const contactPoint = b2ContactManager.s_evalCP;
      contactPoint.shape1 = shape1;
      contactPoint.shape2 = shape2;
      contactPoint.friction = contact.m_friction;
      contactPoint.restitution = contact.m_restitution;
      for (let manifoldIndex = 0; manifoldIndex < manifoldCount; manifoldIndex += 1) {
        const manifold = manifolds[manifoldIndex];
        contactPoint.normal.SetV(manifold.normal ?? manifold.m_normal ?? manifold.m_localPlaneNormal);
        const pointCount = manifold.m_pointCount ?? manifold.pointCount ?? 0;
        for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
          const point = manifold.m_points?.[pointIndex] ?? manifold.points?.[pointIndex];
          const localPoint1 = point.m_localPoint ?? point.localPoint1;
          const localPoint2 = point.localPoint2 ?? point.m_localPoint;
          contactPoint.position = body1.GetWorldPoint(localPoint1);
          const velocity1 = body1.GetLinearVelocityFromLocalPoint(localPoint1);
          const velocity2 = body2.GetLinearVelocityFromLocalPoint(localPoint2);
          contactPoint.velocity.Set(velocity2.x - velocity1.x, velocity2.y - velocity1.y);
          contactPoint.separation = point.m_separation ?? point.separation;
          contactPoint.id.key = point.m_id?._key ?? point.id?._key ?? point.m_id?.key ?? point.id?.key ?? 0;
          this.m_world.m_contactListener.Remove(contactPoint);
        }
      }
    }
    if (contact.m_prev) {
      contact.m_prev.m_next = contact.m_next;
    }
    if (contact.m_next) {
      contact.m_next.m_prev = contact.m_prev;
    }
    if (contact === this.m_world.m_contactList) {
      this.m_world.m_contactList = contact.m_next;
    }
    const body1 = getContactBody1(contact);
    const body2 = getContactBody2(contact);
    // AS3 uses m_node1/m_node2
    const node1 = contact.m_node1;
    const node2 = contact.m_node2;
    if (node1?.prev) { node1.prev.next = node1.next; }
    if (node1?.next) { node1.next.prev = node1.prev; }
    if (node1 === body1?.m_contactList) { body1.m_contactList = node1.next; }
    if (node2?.prev) { node2.prev.next = node2.next; }
    if (node2?.next) { node2.next.prev = node2.prev; }
    if (node2 === body2?.m_contactList) { body2.m_contactList = node2.next; }
    const destroy = contact.constructor.Destroy ?? (() => { });
    destroy(contact, this.m_world?.m_blockAllocator);
    this.m_world.m_contactCount -= 1;
  }

  // AS3 Collide: updates existing contacts, skips sleeping pairs
  Collide() {
    const listener = this.m_world?.m_contactListener ?? nullContactListener;
    let contact = this.m_world.m_contactList;
    while (contact) {
      const body1 = getContactBody1(contact);
      const body2 = getContactBody2(contact);
      if (!(body1.IsSleeping() && body2.IsSleeping())) {
        contact.Update(listener);
      }
      contact = contact.m_next;
    }
  }
}
